import { ILocationV2 } from '@sravni/types/lib/locations';
import {
  BANKRUPTCY_PAGE_TYPE,
  BANKS_PAGE_TYPE,
  BOOKMAKER_PAGE_TYPE,
  BROKER_PAGE_TYPE,
  FOOD_RATION_PAGE_TYPE,
  INSURANCE_COMPANY_PAGE_TYPE,
  LEASING_PAGE_TYPE,
  MARKETPLACE_PAGE_TYPE,
  NPF_PAGE_TYPE,
  PAGE_TYPE,
  SCHOOL_PAGE_TYPE,
  UNIVERSITY_PAGE_TYPE,
} from '@sravni/ugc/lib/organizations/constants';
import { checkIfMfo } from '@sravni/ugc/lib/utils/checkOrganizationsType';

import type { GetBannersParams } from '../__generated__/gateway/gatewayApiTypes';
import {
  SravniOrganizationsSharedReviewObjectType,
  SravniReviewsEnumsReviewRateStatus,
} from '../__generated__/reviews/reviewsApiTypes';
import {
  ANSWERS_OPTIONS_LABELS,
  DEFAULT_FILTERS,
  FILTER_NAMES,
  FILTER_STATUS_OPTIONS,
  ORDER_OPTIONS,
  ORDER_OPTIONS_LABELS,
  OTHER_TAGS,
  PRODUCT_PAGE_HEADING,
  PRODUCT_TYPE,
  UNNECESSARY_FILTERS,
} from '../constants/reviews';
import { ITEM, ITEM_MFO, LIST, ORGANIZATION_LIST, ORGANIZATION_LIST_MFO } from '../constants/routes';
import type { TBannerTypes } from '../interfaces/advertising';
import { IOffer } from '../interfaces/offers';
import { OrganizationType } from '../interfaces/organizations';
import {
  ICompanyPosition,
  IProductRating,
  IReviewFilters,
  IReviewItem,
  IReviewsCount,
  IServicesRating,
  ITag,
  TReviewObjectType,
} from '../interfaces/reviews';
import { TAdProducts } from '../server/services/doubleClick';
import { createLinkProps } from '../utils/routing';
import { sortArray } from '../utils/sort';

export type ReviewType = {
  typeForService: OrganizationType;
  listTypeUrl?: PAGE_TYPE;
  itemTypeUrl?: PAGE_TYPE;
  logoTypeUrl?: string;
  ratingUrl?: string;
  seoService?: string;
  reviewService?: TReviewObjectType;
  dfpCategory?: 'none' | 'banks' | 'insurance';
  namesType?: {
    bigFirstChar: string;
    smallFirstChar: string;
    mode: string;
    mode2: string;
    mode3: string;
    mode4: string;
    mode5: string;
    mode6: string;
    mode7: string;
    mode8: string;
    mode9: string;
    mode10: string;
    gender: 'masculine' | 'feminine' | 'neuter';
  };
};

export const getReviewType = (
  type: PAGE_TYPE | PRODUCT_TYPE | SravniOrganizationsSharedReviewObjectType,
  products?: { [key in OrganizationType]?: string[] },
): ReviewType => {
  if (
    BANKS_PAGE_TYPE.includes(type as PAGE_TYPE) ||
    products?.[OrganizationType.BANK]?.includes(type as PRODUCT_TYPE)
  ) {
    return {
      typeForService: OrganizationType.BANK,
      listTypeUrl: PAGE_TYPE.Banki,
      itemTypeUrl: PAGE_TYPE.Bank,
      logoTypeUrl: 'banks',
      ratingUrl: '/banki/rating/klientskij/',
      reviewService: TReviewObjectType.Banks,
      seoService: 'bank',
      dfpCategory: 'banks',
      namesType: {
        bigFirstChar: 'Банк',
        smallFirstChar: 'банк',
        mode: 'банка',
        mode2: 'банком',
        mode3: 'банковские',
        mode4: 'банковских',
        mode5: 'банк',
        mode6: 'банке',
        mode7: 'банков',
        mode8: 'банках',
        mode9: 'банков',
        mode10: 'банка',
        gender: 'masculine',
      },
    };
  }
  if (
    INSURANCE_COMPANY_PAGE_TYPE.includes(type as PAGE_TYPE) ||
    products?.[OrganizationType.INSURANCE]?.includes(type as PRODUCT_TYPE)
  ) {
    return {
      typeForService: OrganizationType.INSURANCE,
      listTypeUrl: PAGE_TYPE.StrakhovyeKompanii,
      itemTypeUrl: PAGE_TYPE.StrakhovajaKompanija,
      logoTypeUrl: 'ic',
      ratingUrl: '/strakhovye-kompanii/rating/klientskij/',
      seoService: 'insurancecompany',
      reviewService: TReviewObjectType.InsuranceCompany,
      dfpCategory: 'insurance',
      namesType: {
        bigFirstChar: 'Страховая компания',
        smallFirstChar: 'страховая компания',
        mode: 'страховой компании',
        mode2: 'страховой компанией',
        mode3: 'страховые',
        mode4: 'страховых',
        mode5: 'страховую компанию',
        mode6: 'страховой компании',
        mode7: 'страховых компаний',
        mode8: 'страховых компаниях',
        mode9: 'страховых компаний',
        mode10: 'страховые компании',
        gender: 'feminine',
      },
    };
  }
  if (checkIfMfo(type as PAGE_TYPE)) {
    return {
      typeForService: OrganizationType.MFO,
      listTypeUrl: PAGE_TYPE.Zaimy,
      itemTypeUrl: PAGE_TYPE.Zaimy,
      logoTypeUrl: 'mfi',
      ratingUrl: '/zaimy/rating-mfo/?userRatingType=all',
      reviewService: TReviewObjectType.Mfi,
      seoService: 'MicrofinanceOrganization',
      // категории mfo нет поэтому ставим banks
      dfpCategory: 'banks',
      namesType: {
        bigFirstChar: 'Микрофинансовая организация',
        smallFirstChar: 'микрофинансовая организация',
        mode: 'микрофинансовой организации',
        mode2: 'микрофинансовой организацией',
        mode3: 'микрофинансовые',
        mode4: 'микрофинансовых',
        mode5: 'микрофинансовую организацию',
        mode6: 'микрофинансовой организации',
        mode7: 'микрофинансовых организаций',
        mode8: 'микрофинансовых организациях',
        mode9: 'микрофинансовых организаций',
        mode10: 'микрофинансовые организации',
        gender: 'feminine',
      },
    };
  }
  if (
    SCHOOL_PAGE_TYPE.includes(type as PAGE_TYPE) ||
    products?.[OrganizationType.SCHOOL]?.includes(type as PRODUCT_TYPE)
  ) {
    return {
      typeForService: OrganizationType.SCHOOL,
      listTypeUrl: PAGE_TYPE.Kursy,
      itemTypeUrl: PAGE_TYPE.Shkola,
      // logoTypeUrl: 'mfi',
      ratingUrl: '/kursy/rating/',
      reviewService: TReviewObjectType.EducationalOrganization,
      // seoService: 'MicrofinanceOrganization',
      // категории school нет поэтому ставим banks
      dfpCategory: 'banks',
      namesType: {
        bigFirstChar: 'Онлайн-школа',
        smallFirstChar: 'онлайн-школа',
        mode: 'онлайн-школы',
        mode2: 'онлайн-школой',
        mode3: 'образовательные',
        mode4: 'образовательных',
        mode5: 'онлайн-школу',
        mode6: 'онлайн-школе',
        mode7: 'онлайн-школой',
        mode8: 'онлайн-школах',
        mode9: 'онлайн-школ',
        mode10: 'онлайн-школы',
        gender: 'feminine',
      },
    };
  }
  if (BROKER_PAGE_TYPE.includes(type as PAGE_TYPE)) {
    return {
      typeForService: OrganizationType.BROKER,
      listTypeUrl: PAGE_TYPE.Brokery,
      itemTypeUrl: PAGE_TYPE.Broker,
      logoTypeUrl: 'banks',
      ratingUrl: '/brokery/rating/klientskij/',
      reviewService: TReviewObjectType.Broker,
      seoService: 'broker',
      dfpCategory: 'banks',
      namesType: {
        bigFirstChar: 'Брокер',
        smallFirstChar: 'брокер',
        mode: 'брокера',
        mode2: 'брокером',
        mode3: 'брокерские',
        mode4: 'брокерских',
        mode5: 'брокер',
        mode6: 'брокере',
        mode7: 'брокеров',
        mode8: 'брокерах',
        mode9: 'брокеров',
        mode10: 'брокера',
        gender: 'masculine',
      },
    };
  }

  if (NPF_PAGE_TYPE.includes(type as PAGE_TYPE)) {
    return {
      typeForService: OrganizationType.NPF,
      listTypeUrl: PAGE_TYPE.Npf,
      itemTypeUrl: PAGE_TYPE.Npf,
      logoTypeUrl: 'banks',
      ratingUrl: '/npf/rating/klientskij/',
      reviewService: TReviewObjectType.Npf,
      seoService: 'npf',
      dfpCategory: 'banks',
      namesType: {
        bigFirstChar: 'Фонд',
        smallFirstChar: 'фонд',
        mode: 'фонда',
        mode2: 'фондом',
        mode3: 'пенсионные',
        mode4: 'пенсионных',
        mode5: 'фонд',
        mode6: 'фонде',
        mode7: 'фондов',
        mode8: 'фондах',
        mode9: 'фондов',
        mode10: 'фонда',
        gender: 'masculine',
      },
    };
  }

  if (LEASING_PAGE_TYPE.includes(type as PAGE_TYPE)) {
    return {
      typeForService: OrganizationType.LEASING,
      listTypeUrl: PAGE_TYPE.Leasing,
      itemTypeUrl: PAGE_TYPE.Leasing,
      ratingUrl: '/leasing/rating/klientskij/',
      reviewService: TReviewObjectType.Leasing,
      seoService: 'leasing',
      dfpCategory: 'banks',
      namesType: {
        bigFirstChar: 'Лизинговая компания',
        smallFirstChar: 'лизинговая компания',
        mode: 'лизинговой компании',
        mode2: 'лизинговая компанией',
        mode3: 'лизинговые',
        mode4: 'лизинговых',
        mode5: 'лизинговую компанию',
        mode6: 'лизинговой компании',
        mode7: 'лизинговых компаний',
        mode8: 'лизинговых компаниях',
        mode9: 'лизинговых компаний',
        mode10: 'лизинговые компании',
        gender: 'feminine',
      },
    };
  }

  if (MARKETPLACE_PAGE_TYPE.includes(type as PAGE_TYPE)) {
    return {
      typeForService: OrganizationType.MARKETPLACE,
      listTypeUrl: PAGE_TYPE.Marketplace,
      itemTypeUrl: PAGE_TYPE.Marketplace,
      ratingUrl: '/marketplace/rating/klientskij/',
      reviewService: TReviewObjectType.Marketplace,
      seoService: 'marketplace',
      dfpCategory: 'banks',
      namesType: {
        bigFirstChar: 'Маркетплейс',
        smallFirstChar: 'маркетплейс',
        mode: 'маркетплейса',
        mode2: 'маркетплейсом',
        mode3: 'маркетплейсовые',
        mode4: 'маркетплейсовых',
        mode5: 'маркетплейс',
        mode6: 'маркетплейсе',
        mode7: 'маркетплейсов',
        mode8: 'маркетплейсах',
        mode9: 'маркетплейсов',
        mode10: 'маркетплейса',
        gender: 'masculine',
      },
    };
  }

  if (BANKRUPTCY_PAGE_TYPE.includes(type as PAGE_TYPE)) {
    return {
      typeForService: OrganizationType.BANKRUPTCY,
      listTypeUrl: PAGE_TYPE.Bankruptcy,
      itemTypeUrl: PAGE_TYPE.Bankruptcy,
      ratingUrl: '/bankruptcy/rating/klientskij/',
      reviewService: TReviewObjectType.Bankruptcy,
      seoService: 'bankruptcy',
      dfpCategory: 'banks',
      namesType: {
        bigFirstChar: 'Компания по банкротству',
        smallFirstChar: 'компания по банкротству',
        mode: 'компании по банкротству',
        mode2: 'компанией по банкротству',
        mode3: 'банкротские',
        mode4: 'банкротских',
        mode5: 'компанию по банкротству',
        mode6: 'компании по банкротству',
        mode7: 'компаний по банкротству',
        mode8: 'компаниях по банкротству',
        mode9: 'компаний по банкротству',
        mode10: 'компании по банкротству',
        gender: 'feminine',
      },
    };
  }

  if (UNIVERSITY_PAGE_TYPE.includes(type as PAGE_TYPE)) {
    return {
      typeForService: OrganizationType.UNIVERSITY,
      listTypeUrl: PAGE_TYPE.University,
      itemTypeUrl: PAGE_TYPE.University,
      ratingUrl: '/university/rating/klientskij/',
      reviewService: TReviewObjectType.University,
      seoService: 'university',
      dfpCategory: 'banks',
      namesType: {
        bigFirstChar: 'Вуз',
        smallFirstChar: 'вуз',
        mode: 'вуза',
        mode2: 'вузом',
        mode3: 'образовательные',
        mode4: 'образовательных',
        mode5: 'вуз',
        mode6: 'вузе',
        mode7: 'вузов',
        mode8: 'вузах',
        mode9: 'вузов',
        mode10: 'вуза',
        gender: 'masculine',
      },
    };
  }

  if (BOOKMAKER_PAGE_TYPE.includes(type as PAGE_TYPE)) {
    return {
      typeForService: OrganizationType.BOOKMAKER,
      listTypeUrl: PAGE_TYPE.Bukmekery,
      itemTypeUrl: PAGE_TYPE.Bukmeker,
      ratingUrl: '/bukmekery/rating/klientskij/',
      reviewService: TReviewObjectType.Bookmaker,
      seoService: 'bookmaker',
      dfpCategory: 'banks',
      namesType: {
        bigFirstChar: 'Букмекер',
        smallFirstChar: 'букмекер',
        mode: 'букмекера',
        mode2: 'букмекером',
        mode3: 'букмекерские',
        mode4: 'букмекерских',
        mode5: 'букмекер',
        mode6: 'букмекере',
        mode7: 'букмекеров',
        mode8: 'букмекерах',
        mode9: 'букмекеров',
        mode10: 'букмекера',
        gender: 'masculine',
      },
    };
  }

  if (FOOD_RATION_PAGE_TYPE.includes(type as PAGE_TYPE)) {
    return {
      typeForService: OrganizationType.FOOD_RATION,
      listTypeUrl: PAGE_TYPE.RacionyKompanii,
      itemTypeUrl: PAGE_TYPE.RacionyKompanii,
      ratingUrl: '/raciony-kompanii/rating/',
      reviewService: TReviewObjectType.FoodRation,
      seoService: 'foodRation',
      dfpCategory: 'banks',
      namesType: {
        bigFirstChar: 'Сервис готовых рационов',
        smallFirstChar: 'сервис готовых рационов',
        mode: 'сервиса готовых рационов',
        mode2: 'сервисом готовых рационов',
        mode3: 'сервисные',
        mode4: 'сервисных',
        mode5: 'сервис готовых рационов',
        mode6: 'сервисе готовых рационов',
        mode7: 'сервисов готовых рационов',
        mode8: 'сервисах готовых рационов',
        mode9: 'сервисов готовых рационов',
        mode10: 'сервиса готовых рационов',
        gender: 'masculine',
      },
    };
  }

  return {} as ReviewType;
};

export type TQueryDictionaries = {
  locations?: ILocationV2[];
};

export const getFiltersFromQuery = (query, dictionaries: TQueryDictionaries = {}) => {
  const { locations } = dictionaries;

  return Object.values(FILTER_NAMES).reduce((filter, key) => {
    if (key in query && query[key] !== UNNECESSARY_FILTERS[key]) {
      const queryValue = query[key];

      if (key === FILTER_NAMES.RATED && !Array.isArray(queryValue)) {
        return { ...filter, [key]: [queryValue] };
      }

      if (key === FILTER_NAMES.LOCATION_GAR_ID && locations) {
        const location = locations?.find((loc) => loc.alias === queryValue);
        return { ...filter, [key]: location?.garId };
      }

      return { ...filter, [key]: queryValue };
    }

    return filter;
  }, {});
};

export const mapFiltersFromQuery = (query, injectFilters = {}) => {
  return Object.keys(DEFAULT_FILTERS).reduce(
    (filter, key) => {
      if (key in query) {
        filter[key] = query[key];
      }

      return filter;
    },
    { ...injectFilters },
  );
};

interface IDfpOptions {
  productAlias: string;
  type: ReviewType;
  adProducts: TAdProducts;
}

export const createParamsDfp = (options: IDfpOptions) => {
  const { type, productAlias, adProducts = {} } = options;

  let productParam = '';
  if (adProducts?.[productAlias]) {
    productParam = adProducts[productAlias].code.toLowerCase();
    // } else if (type === PRODUCT_TYPE.VALJUTY) {
    //   productParam = 'exchange';
  } else {
    productParam = 'service';
  }

  return {
    Category: type.dfpCategory,
    Product: productParam as GetBannersParams['Product'],
    Page: 'reviews' as GetBannersParams['Page'],
    BannerTypes: ['side', 'tgbm7', 'outOfPage', 'top'] as TBannerTypes[],
  };
};

export const findNameSettings = (settings: ITag[], param: string) => {
  if (param === 'valjuty') {
    param = 'obmen-valjuty';
  }
  return settings?.find((tag) => {
    if (tag.key === param || tag.alias === param) {
      return tag.name;
    }
  })?.name;
};

export const getTagByProduct = (tags: ITag[] = [], product: string) => {
  return tags.find((tag) => tag.alias === product);
};

export const setReviewFilterTag = (serverFiltersTags: ITag[], tag: string) => {
  const reviewTag = serverFiltersTags?.find((item) => item.alias === tag);

  return reviewTag?.key || '';
};

export const createReviewFilterParam = (name: string, serverFilters: IReviewFilters) => {
  const filters = [];

  if (serverFilters?.[name]) {
    serverFilters[name]?.map((item) => {
      const filter = {
        value: item.key,
        alias: item.alias,
        name: item.name,
        legalValue: item.legalValue,
      };

      if (name === 'reviewOrder' && !Object.values(ORDER_OPTIONS).includes(item.key)) {
        return null;
      }

      if (name === 'reviewFilter' && !Object.values(FILTER_STATUS_OPTIONS).includes(item.key)) {
        return null;
      }

      return filters.push(filter);
    });
  }

  return filters;
};

export const shrinkText = (text: string, visible: number = 60, threshold: number = 10) => {
  const title = text;

  if (title.length < visible + threshold) {
    return title;
  } else {
    // eslint-disable-line
    let str = title.slice(visible - threshold);
    const index = str.search(/[\.:,\-\!\?]/g);
    if (index > 0 && index < threshold * 2) {
      str = title.slice(0, visible - threshold + index + 1);
    } else {
      str = title.slice(0, visible);
    }
    if (str[str.length - 1].search(/[\.:,\-\!\?]/g) === 0) {
      str = str.slice(0, -1);
    }

    return `${str}…`;
  }
};

export const REVIEW_TYPE_LIST_ROUTE_MAP = {
  [PAGE_TYPE.Banks]: PAGE_TYPE.Banki,
  [PAGE_TYPE.Mfi]: PAGE_TYPE.Zaimy,
  [PAGE_TYPE.Insurance]: PAGE_TYPE.StrakhovyeKompanii,
  [PAGE_TYPE.EducationalOrganization]: PAGE_TYPE.Kursy,
  [PAGE_TYPE.Broker]: PAGE_TYPE.Brokery,
  [PAGE_TYPE.Npf]: PAGE_TYPE.Npf,
  [PAGE_TYPE.Leasing]: PAGE_TYPE.Leasing,
  [PAGE_TYPE.Marketplace]: PAGE_TYPE.Marketplace,
  [PAGE_TYPE.Bankruptcy]: PAGE_TYPE.Bankruptcy,
  [PAGE_TYPE.University]: PAGE_TYPE.University,
  [PAGE_TYPE.Bookmaker]: PAGE_TYPE.Bukmekery,
  [PAGE_TYPE.FoodRation]: PAGE_TYPE.RacionyKompanii,
};

const getParamName = (item, typePage: PAGE_TYPE) => {
  const orderLabels = Object.keys(ORDER_OPTIONS_LABELS);
  const answersLabels = Object.keys(ANSWERS_OPTIONS_LABELS);

  if (orderLabels.includes(item.value)) return ORDER_OPTIONS_LABELS[item.value];
  if (item.value !== 'withAnswers') return item.name || item.label;
  if (answersLabels.includes(typePage)) return ANSWERS_OPTIONS_LABELS[typePage];

  return 'С ответами организации';
};

export const getSelectOptions = (options, typePage) =>
  options.map((item) => ({
    value: item.value || item.key,
    alias: item.alias,
    label: getParamName(item, typePage),
    legalValue: item.legalValue,
  }));

export const getServicesOptions = (
  serverFilters: IReviewFilters,
  reviewsCount: IReviewsCount[],
  typePage: PAGE_TYPE,
  servicesRating?: IServicesRating[],
) => {
  const options = getSelectOptions(createReviewFilterParam('tag', serverFilters), typePage)
    .map((item) => ({
      ...item,
      reviews: reviewsCount.find((el) => el.serviceTag === item.value)?.reviewCount || 0,
      rating: servicesRating?.find((el) => el.reviewTag === item.value)?.rating || 0,
    }))
    .filter((item) => item.reviews);

  return sortArray(options, 'reviews', true);
};

export const checkIfModerationComplete = (review: IReviewItem) =>
  [SravniReviewsEnumsReviewRateStatus.RateApproved, SravniReviewsEnumsReviewRateStatus.RateRejected].includes(
    review?.ratingStatus,
  );

interface IBackLinkProps {
  type: PAGE_TYPE;
  organization?: string;
  product?: string;
}

export type TBackLink = {
  href: string;
  label: string;
};

export const listBackLink = ({ type, organization, product }: IBackLinkProps): TBackLink => {
  const { listTypeUrl, itemTypeUrl, namesType, ratingUrl } = getReviewType(type);
  const isMfo = checkIfMfo(type);

  if (!namesType) return null;

  let href = `/${listTypeUrl}/`;
  let label = `К списку ${namesType.mode9}`;

  if (isMfo) {
    href = '/zaimy/mfo/';
  } else if (type === PAGE_TYPE.RacionyKompanii) {
    href = ratingUrl;
    label = 'К рейтингу серисов готовых рационов';
  }

  if (product) {
    const { as } = createLinkProps('/list', isMfo ? ORGANIZATION_LIST_MFO : ORGANIZATION_LIST, {
      type: itemTypeUrl,
      organization,
    });

    href = as;
    label = `К отзывам о ${namesType.mode6}`;
  } else if (organization) {
    const { as } = createLinkProps('/list', LIST, {
      type: listTypeUrl,
    });

    href = as;
    label = `К отзывам ${type === PAGE_TYPE.Shkola ? 'об' : 'о'} ${namesType.mode8}`;
  }

  return {
    href,
    label,
  };
};

export const getTagsData = (tags, isLegal) =>
  tags?.tag?.map((item) => ({
    ...item,
    legalValue: OTHER_TAGS.includes(item.key) ? 'other' : isLegal.toString(),
  }));

export const getBanksTagsList = (tagsIndividual, tagsLegal) => {
  const tagsIndividualList = getTagsData(tagsIndividual, false);
  const tagsLegalList = getTagsData(tagsLegal, true)?.filter(
    ({ key }) => !tagsIndividualList.find((el) => el.key === key),
  );

  return tagsIndividualList.concat(tagsLegalList);
};

export const getProductHeading = (product: string, typePage: PAGE_TYPE, productDetails: IOffer) => {
  const serviceName = PRODUCT_PAGE_HEADING[product] ? `${PRODUCT_PAGE_HEADING[product]} ` : '';
  const productName = typePage === PAGE_TYPE.Shkola ? productDetails?.name : `«${productDetails?.name}»`;

  return `${serviceName}${productName}: отзывы`;
};

export const getRating = (
  position: ICompanyPosition,
  productRating: IProductRating,
  servicesRating: IServicesRating[],
  specificProduct: string,
  productTag: string,
): number => {
  let rating = position?.complexCalculatedRatingValue;
  if (specificProduct) {
    rating = productRating?.rating;
  } else if (specificProduct) {
    rating = servicesRating?.find((item) => item.reviewTag === productTag)?.rating;
  }

  return rating;
};

interface IGetReviewUrl {
  pageType: PAGE_TYPE;
  typeAlias: PAGE_TYPE;
  organizationAlias: string;
  reviewId: number;
}

export const getReviewUrl = ({ pageType, typeAlias, organizationAlias, reviewId }: IGetReviewUrl) => {
  const isMfo = checkIfMfo(pageType);
  const itemRoute = isMfo ? ITEM_MFO : ITEM;

  if (typeAlias && organizationAlias && reviewId) {
    return createLinkProps('/review', itemRoute, {
      type: typeAlias,
      organization: organizationAlias,
      id: reviewId,
    });
  }

  return null;
};
